import type { AchievementView } from '../../shared/achievements.ts';
import { $, el } from '../common/dom.ts';
import { icon } from '../common/icons.ts';
import { Modal } from '../common/modal.ts';

/** Succès du joueur : débloqués d'abord (avec leur date), puis les autres avec leur progression. */
export class AchievementsPanel extends Modal {
  private readonly grid = $('.achievement-grid', this.root);
  private readonly summary = $('.achievement-summary', this.root);
  private readonly load: () => Promise<AchievementView[]>;

  constructor(load: () => Promise<AchievementView[]>) {
    super('#achievements');
    this.load = load;
    $('#btn-achievements').addEventListener('click', () => this.toggle());
  }

  protected override reveal(): Promise<void> {
    return this.show();
  }

  async show(): Promise<void> {
    this.open();
    const current = this.startLoad();
    this.grid.replaceChildren(el('p', { className: 'muted', textContent: 'Chargement…' }));
    const achievements = await this.load().catch(() => null);
    if (!current()) return;
    if (!achievements) {
      this.grid.replaceChildren(el('p', { className: 'muted', textContent: 'Succès indisponibles.' }));
      return;
    }
    // Un succès sans but (aucun badge n'existe encore…) n'est pas montré.
    const shown = achievements.filter((achievement) => achievement.goal > 0);
    const unlocked = shown.filter((achievement) => achievement.unlockedAt !== null);
    this.summary.textContent = `${unlocked.length} / ${shown.length} débloqués`;
    const sorted = [...unlocked.sort((a, b) => (b.unlockedAt ?? 0) - (a.unlockedAt ?? 0)), ...shown.filter((achievement) => achievement.unlockedAt === null)];
    this.grid.replaceChildren(...sorted.map((achievement) => this.card(achievement)));
  }

  private card({ name, description, icon: iconName, progress, goal, unlockedAt }: AchievementView): HTMLElement {
    const done = unlockedAt !== null;
    const bar = el('div', { className: 'bar' }, el('div'));
    (bar.firstElementChild as HTMLElement).style.width = `${(100 * Math.min(progress, goal)) / goal}%`;
    return el('article', { className: `achievement ${done ? 'unlocked' : ''}` },
      el('span', { className: 'achievement-icon' }, icon(done ? iconName : 'lock')),
      el('div', { className: 'achievement-text' },
        el('strong', { textContent: name }),
        el('small', { textContent: description }),
        done ? el('small', { className: 'achievement-date', textContent: `Débloqué le ${new Date(unlockedAt).toLocaleDateString('fr-FR')}` })
          : el('div', { className: 'achievement-progress' }, bar, el('span', { textContent: `${Math.min(progress, goal)} / ${goal}` }))));
  }
}
